import * as React from 'react'
import { useImperativeHandle, useRef } from 'react'
import type { JSX } from 'react'
import { useDrag, useDrop, DragPreviewImage } from 'react-dnd'

import { ArrowLeftRightIcon } from '@buffer-mono/popcorn'

import type { CustomLink } from '~publish/legacy/profile-sidebar/types'

import LinkPreview from '../LinkPreview'
import type { LinkPreviewProps } from '../LinkPreview'

import styles from './index.module.css'

interface DraggedCustomLink {
  item: CustomLink
  onSwapCustomLinks: (from: CustomLink, to: CustomLink) => void
}

type LinkDragWrapperProps = Omit<LinkPreviewProps, 'isOver'> & {
  onSwapCustomLinks: (from: CustomLink, to: CustomLink) => void
}

const LinkDragWrapper = React.forwardRef(
  (
    { onSwapCustomLinks, ...customLinkProps }: LinkDragWrapperProps,
    ref,
  ): JSX.Element => {
    const elementRef = useRef<HTMLDivElement>(null)
    const { item, hasWriteAccess } = customLinkProps

    const [{ isDragging }, connectDragSource, connectDragPreview] = useDrag<
      DraggedCustomLink,
      unknown,
      { isDragging: boolean }
    >(
      () => ({
        type: 'customLink',
        item: { item, onSwapCustomLinks },
        collect: (monitor): { isDragging: boolean } => ({
          isDragging: monitor.isDragging(),
        }),
      }),
      [item, onSwapCustomLinks],
    )

    const [{ isOver }, connectDropTarget] = useDrop<
      DraggedCustomLink,
      void,
      { isOver: boolean }
    >(
      () => ({
        accept: 'customLink',
        drop: (draggedItem): void => {
          draggedItem.onSwapCustomLinks(draggedItem.item, item)
        },
        collect: (monitor): { isOver: boolean } => ({
          isOver: monitor.isOver(),
        }),
      }),
      [item],
    )

    connectDragSource(elementRef)
    connectDropTarget(elementRef)
    useImperativeHandle(ref, () => ({
      getNode: (): HTMLDivElement | null => elementRef.current,
    }))

    return (
      <>
        <DragPreviewImage
          src="https://buffer-publish.s3.amazonaws.com/images/drag-link-placeholder.png"
          connect={connectDragPreview}
        />
        <div
          className={styles.dragWrapper}
          ref={elementRef}
          draggable
          style={{
            transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            outline: 'none',
          }}
          onDragStart={(event): void => {
            if (!hasWriteAccess) {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
        >
          {!isDragging && isOver && (
            <div className={styles.swapWrapper}>
              <div className={styles.swapIcon}>
                <ArrowLeftRightIcon
                  size="small"
                  color="inverted"
                  className={styles.verticalIcon}
                />
                <div className={styles.swapText}>Swap Links</div>
              </div>
            </div>
          )}
          <LinkPreview {...customLinkProps} isOver={isOver} />
        </div>
      </>
    )
  },
)

LinkDragWrapper.displayName = 'LinkDragWrapper'

export default LinkDragWrapper
